import { useEffect, useMemo, useState } from "react"
import { BellRing, Download, Smartphone } from "lucide-react"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { subscribeToPush, syncExistingPushSubscription } from "../../pwa/pushNotifications"
import { registerServiceWorker } from "../../pwa/registerServiceWorker"

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>
}

const IOS_TEXT = "No iPhone: abra no Safari, toque em Compartilhar e depois em 'Adicionar à Tela de Início'."
const DISMISS_PROMPT_STORAGE_KEY = "orderpro:pwa_prompt_dismissed"

function isRunningStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || (window.navigator as any).standalone
}

async function ensureActiveServiceWorkerRegistration() {
  if (!("serviceWorker" in navigator)) {
    return null
  }

  const existing = await navigator.serviceWorker.getRegistration("/sw.js")

  const registration = existing ?? (await registerServiceWorker())

  if (!registration) {
    return null
  }

  if (registration.active) {
    return registration
  }

  const worker = registration.installing ?? registration.waiting
  if (!worker) {
    return registration
  }

  await new Promise<void>((resolve, reject) => {
    const timeout = window.setTimeout(() => reject(new Error("timeout_sw_activation")), 10000)

    worker.addEventListener("statechange", () => {
      if (worker.state === "activated") {
        window.clearTimeout(timeout)
        resolve()
      }
    })
  }).catch(() => null)

  return registration
}

export function PwaInstallPrompt() {
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null)

  const [isStandalone, setIsStandalone] = useState(false)

  const [isIOS, setIsIOS] = useState(false)

  const [isLoggedUser, setIsLoggedUser] = useState(false)

  const [pushEnabled, setPushEnabled] = useState(false)

  const [dismissInstallHint, setDismissInstallHint] = useState(false)

  const [isLoadingPush, setIsLoadingPush] = useState(false)

  const [pushFeedback, setPushFeedback] = useState<string>("")

  const [dismissPrompt, setDismissPrompt] = useState(false)

  useEffect(() => {
    setIsStandalone(isRunningStandalone())

    setIsIOS(/iphone|ipad|ipod/i.test(window.navigator.userAgent))

    const hasRestaurantToken = Boolean(localStorage.getItem(API_ROUTES.TOKEN))

    const hasCourierToken = Boolean(localStorage.getItem(API_ROUTES.TOKEN_COURIER))

    const hasCustomerToken = Boolean(localStorage.getItem(API_ROUTES.TOKEN_CUSTOMER))

    const hasAdminToken = Boolean(localStorage.getItem(API_ROUTES.TOKEN_ADMIN))

    const canUsePwaPrompt = hasCourierToken || (hasRestaurantToken && !hasCustomerToken && !hasAdminToken)

    setIsLoggedUser(canUsePwaPrompt)
    setDismissPrompt(localStorage.getItem(DISMISS_PROMPT_STORAGE_KEY) === "1")

    const onBeforeInstall = (event: Event) => {
      event.preventDefault()

      setInstallEvent(event as BeforeInstallPromptEvent)
    }

    window.addEventListener("beforeinstallprompt", onBeforeInstall)

    void registerServiceWorker()

    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall)
  }, [])

  useEffect(() => {
    if (!isLoggedUser || !("serviceWorker" in navigator)) {
      return
    }

    void ensureActiveServiceWorkerRegistration().then(async (registration) => {
      if (!registration) {
        return
      }

      const subscription = await registration.pushManager.getSubscription()

      setPushEnabled(Boolean(subscription))

      if (subscription) {
        const restored = await syncExistingPushSubscription(registration).catch(() => false)

        setPushEnabled(restored)
      }
    })
  }, [isLoggedUser])

  const shouldShow = useMemo(() => {
    if (!isLoggedUser || dismissPrompt) {
      return false
    }

    const shouldAskPushActivation = !pushEnabled

    const shouldSuggestInstall = !isStandalone && !dismissInstallHint

    return shouldAskPushActivation || shouldSuggestInstall
  }, [dismissInstallHint, dismissPrompt, isLoggedUser, isStandalone, pushEnabled])

  const handleDismissPrompt = () => {
    localStorage.setItem(DISMISS_PROMPT_STORAGE_KEY, "1")
    setInstallEvent(null)
    setDismissInstallHint(true)
    setDismissPrompt(true)
  }

  const handleInstall = async () => {
    if (!installEvent) {
      return
    }

    await installEvent.prompt()

    await installEvent.userChoice

    setInstallEvent(null)
  }

  const handleEnablePush = async () => {
    if (!("Notification" in window)) {
      setPushFeedback("Seu navegador não suporta notificações push.")
      return
    }

    setIsLoadingPush(true)

    setPushFeedback("")

    const permission =
      Notification.permission === "granted" ? "granted" : await Notification.requestPermission().catch(() => "denied")

    if (permission !== "granted") {
      setPushFeedback("Permissão de notificação negada. Ative nas configurações do navegador.")

      setIsLoadingPush(false)
      return
    }

    try {
      const registration = await ensureActiveServiceWorkerRegistration()

      if (!registration) {
        setPushFeedback("Não foi possível inicializar o serviço de notificações neste dispositivo.")
        return
      }

      const success = await subscribeToPush(registration)

      setPushEnabled(success)

      setPushFeedback(
        success ? "Notificações ativadas com sucesso." : "Não foi possível ativar agora. Tente novamente.",
      )
    } catch {
      setPushFeedback("Falha ao ativar notificações. Tente novamente em instantes.")
    } finally {
      setIsLoadingPush(false)
    }
  }

  if (!shouldShow) {
    return null
  }

  return (
    <div className="fixed bottom-5 right-5 z-[9999] w-[calc(100vw-2rem)] max-w-[400px] overflow-hidden rounded-2xl border-2 border-gray-500 bg-white text-slate-800 shadow-2xl">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,200,0,0.40),transparent_40%)]" />

      <div className="relative p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-orange-100 text-orange-600 shadow-inner">
              <Smartphone size={20} />
            </div>

            <div className="min-w-0">
              <strong className="mb-1 block text-sm font-semibold tracking-tight sm:text-base">
                Instale o app no celular
              </strong>

              <p className="text-xs leading-5 text-slate-600 sm:text-sm">
                Abra em tela cheia e receba alertas de pedidos com mais rapidez.
              </p>
            </div>
          </div>

          <button
            aria-label="Fechar recomendação de instalação"
            onClick={handleDismissPrompt}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-orange-100 hover:text-orange-600"
            data-interaction-description-key="interactionDescriptions:components.pwa.pwaInstallPrompt.button.fecharRecomendacaoDeInstalacao"
          >
            <span className="text-lg leading-none">×</span>
          </button>
        </div>

        {isIOS && (
          <p className="mt-3 rounded-xl border border-orange-100 bg-orange-50 px-3 py-2 text-[11px] leading-4 text-orange-700 sm:text-xs">
            {IOS_TEXT}
          </p>
        )}

        {/* Botões */}
        <div className="mt-4 flex flex-wrap gap-2.5">
          {installEvent && !isStandalone && (
            <button
              onClick={() => void handleInstall()}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-orange-300/40 transition hover:scale-[1.03] hover:brightness-110 active:scale-[0.98]"
              data-interaction-description-key="interactionDescriptions:components.pwa.pwaInstallPrompt.button.voidHandleInstall"
            >
              <Download size={16} />
              Instalar
            </button>
          )}

          <button
            onClick={() => void handleEnablePush()}
            disabled={isLoadingPush}
            className={[
              "inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition",
              isLoadingPush
                ? "cursor-not-allowed border-orange-100 bg-orange-50 text-orange-300"
                : pushEnabled
                  ? "border-orange-200 bg-orange-100 text-orange-700 hover:bg-orange-200"
                  : "border-orange-200 bg-white text-orange-600 hover:bg-orange-50",
            ].join(" ")}
            data-interaction-description-key="interactionDescriptions:components.pwa.pwaInstallPrompt.button.voidHandleEnablePush"
          >
            <BellRing size={16} />
            {pushEnabled ? "Notificações ativas" : isLoadingPush ? "Ativando..." : "Ativar notificações"}
          </button>
        </div>

        {pushFeedback && (
          <p className={`mt-3 text-xs leading-5 ${pushEnabled ? "text-green-600" : "text-orange-600"}`}>
            {pushFeedback}
          </p>
        )}
      </div>
    </div>
  )
}
